/**
 * Top-level shell — UI-PHASE-16.A.
 *
 * Pre-16.A this was a 3-column CSS grid (left rail / center stage /
 * right rail). 16.A breaks the left rail + center into a single
 * gridstack-managed canvas where every panel is a draggable, resizable
 * cube. Layout persists to localStorage; "Reset layout" wipes it.
 *
 * The right rail (mobile preview) stays outside the grid for now —
 * it's a fixed 420 px wide and the iframe inside doesn't shrink well.
 * Moving it into the grid is 16.B.
 */
import { html, render, type TemplateResult } from "lit-html";
import { renderHeader }         from "./components/Header";
import { renderRightRail }      from "./components/RightRail";
import { renderExplainDrawer }  from "./components/ExplainButton";
import { renderCmdK }           from "./components/CmdK";
import { renderMorningBriefDrawer } from "./components/MorningBrief";
import { renderLaunchAgentLogDrawer } from "./components/LaunchAgentLogDrawer";
import { store } from "./data/store";
import { layoutManager } from "./layout/LayoutManager";

export type TabId =
  | "schedule"
  | "pipeline"
  | "calibration"
  | "cost"
  | "bets"
  | "prompts"
  | "strategies"
  | "replay"
  | "rewind"
  | "alerts"
  | "experiments"
  | "lineups"
  | "rotation"
  | "preview"
  | "eggs"
  | "todo"
  | "diff";

let rootEl: HTMLElement | null = null;
let gridMounted = false;

export function renderApp(root: HTMLElement): void {
  rootEl = root;
  store.subscribe(paint);
  store.start();
  paint();

  // Global overlay hosts (drawers + Cmd-K). Same as before.
  mountOverlayHost("explain-drawer-host",         renderExplainDrawer);
  mountOverlayHost("cmdk-host",                   renderCmdK);
  mountOverlayHost("morning-brief-drawer-host",   renderMorningBriefDrawer);
  mountOverlayHost("launchagent-log-drawer-host", renderLaunchAgentLogDrawer);
}

function mountOverlayHost(id: string, mountFn: (el: HTMLElement) => void): void {
  const host = document.createElement("div");
  host.id = id;
  document.body.appendChild(host);
  mountFn(host);
}

function paint(): void {
  if (!rootEl) return;
  render(template(), rootEl);

  // The gridstack canvas is built once, on the first paint after the
  // grid host element exists in the DOM. After that we never recreate
  // it — paintAll() refreshes each cube's content in place.
  const gridHost = rootEl.querySelector(".grid-canvas") as HTMLElement | null;
  if (gridHost && !gridMounted) {
    layoutManager.mount(gridHost);
    gridMounted = true;
  }
  if (gridMounted) layoutManager.paintAll();
}

/** Imperative tab switch — used by CmdK deep-links into Bet Inspector. */
export function setActiveTab(tab: TabId): void {
  layoutManager.setActiveTab(tab);
}

function template(): TemplateResult {
  const whoami = store.get<{ name: string; email: string }>("whoami");
  return html`
    <div class="console-shell">
      ${renderHeader({
        identityName: whoami.data?.name ?? null,
        onResetLayout: () => layoutManager.resetLayout(),
      })}
      <div class="grid-canvas"></div>
      ${renderRightRail()}
    </div>
  `;
}
